import { Database, Table2, Filter, ArrowUpDown, ChevronRight } from 'lucide-react';
import { ConnectionColorSwatch } from '../../../connections/components/ConnectionColorSwatch';
import { formatRowCount } from '../../common/utils/formatRowCount';
import { getDatabaseDisplayName } from '../../common/utils/databaseDisplayName';
import { ESTIMATED_TOTAL_TITLE } from '../utils/paginationModel';

/**
 * Table Explorer's bottom status bar — identity (table, connection ›
 * database) and query shape (filters, sorts).
 *
 * The connection › database pair uses the Query Editor status bar's order
 * and colour square, as plain text: a Table Explorer tab is bound to its
 * table, so there is nothing to switch here, and the absence of the Query
 * Editor's pill says so.
 *
 * It deliberately carries **no row count in the default table-grid view**:
 * the pagination strip immediately above it owns that fact there, in its
 * richer positional form ("51-100 of 237"), directly beside the pager it
 * describes. The two bars are stacked ~20px apart, so a count in both
 * restated the same number twice within one glance — the exact defect PP-02
 * exists to remove. (The Query Editor keeps a count in both its grid toolbar
 * and its status bar because those sit at opposite ends of the tab and are
 * scoped differently — active result set vs. whole execution.)
 *
 * Carve-out: the pagination strip doesn't render at all in cross-table drill
 * and single-row modes (there's no page to navigate). In those two modes this
 * bar is the *only* surface left, so the caller passes `rowCount`/
 * `totalRowCount` — the negation of the strip's own render guard — and this
 * bar picks up the count rather than let it vanish. `rowCount` stays
 * `undefined` whenever the strip is showing, so exactly one surface ever
 * states the count.
 */
type TableExplorerStatusBarProps = {
  tableName: string;
  schema: string;
  rowCount?: number;
  totalRowCount?: number;
  /**
   * True when `totalRowCount` is a catalog-statistics ESTIMATE (the exact
   * `COUNT(*)` missed its budget on an unfiltered fetch). The count then reads
   * "N of ≈M" and explains itself on hover.
   */
  totalIsEstimate?: boolean;
  connectionName?: string;
  /** Connection colour; draws the same square as the Query Editor status bar. */
  connectionColor?: string;
  database?: string;
  engine?: string;
  filterCount?: number;
  sortCount?: number;
};

export function TableExplorerStatusBar({
  tableName,
  schema,
  rowCount,
  totalRowCount,
  totalIsEstimate = false,
  connectionName,
  connectionColor,
  database,
  engine = 'mssql',
  filterCount = 0,
  sortCount = 0,
}: TableExplorerStatusBarProps) {
  const fullTableName = schema ? `${schema}.${tableName}` : tableName;
  const { displayName: databaseDisplayName, fullPath: databaseFullPath } = getDatabaseDisplayName(database, engine);

  // Pluralizes correctly ("1 row", "0 rows", "3 rows") instead of always
  // appending "rows", matching the Query Editor status bar's row-count widget
  // (two-grid parity, CLAUDE.md). Only computed when the caller actually
  // wants this bar to carry the count (see carve-out above).
  const rowCountText = () => {
    if (rowCount === undefined) return null;
    if (totalRowCount !== undefined && totalRowCount > rowCount) {
      // "≈" marks a total that came from table statistics rather than COUNT(*).
      return `${rowCount.toLocaleString()} of ${totalIsEstimate ? '≈' : ''}${formatRowCount(totalRowCount)}`;
    }
    return formatRowCount(rowCount);
  };

  return (
    <div className="flex items-center gap-4 px-2 h-[20px] text-xs text-muted-foreground bg-muted/30 border-t border-border shrink-0" data-testid="table-explorer-status-bar">
      {/* Table name */}
      <div className="flex items-center gap-1">
        <Table2 className="h-3 w-3" />
        <span>{fullTableName}</span>
      </div>

      {/* Row count — only when the pagination strip isn't showing (see the
          carve-out in this component's doc comment above) */}
      {rowCount !== undefined && (
        <span
          className="tabular-nums"
          data-testid="te-status-bar-row-count"
          {...(totalIsEstimate ? { title: ESTIMATED_TOTAL_TITLE, 'data-estimated': 'true' } : {})}
        >
          {rowCountText()}
        </span>
      )}

      {/* Filter count */}
      {filterCount > 0 && (
        <div className="flex items-center gap-1">
          <Filter className="h-3 w-3" />
          <span>{filterCount} {filterCount === 1 ? 'filter' : 'filters'}</span>
        </div>
      )}

      {/* Sort count */}
      {sortCount > 0 && (
        <div className="flex items-center gap-1">
          <ArrowUpDown className="h-3 w-3" />
          <span>{sortCount} {sortCount === 1 ? 'sort' : 'sorts'}</span>
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Connection › Database */}
      <div className="flex items-center gap-1" data-testid="te-status-bar-context">
        {connectionName && (
          <span className="flex items-center gap-1 text-muted-foreground/70" data-testid="te-status-bar-connection">
            {connectionColor && <ConnectionColorSwatch color={connectionColor} />}
            <span>{connectionName}</span>
          </span>
        )}
        {connectionName && database && <ChevronRight aria-hidden="true" className="h-2.5 w-2.5" />}
        {database && (
          <div
            className="flex items-center gap-1"
            title={databaseFullPath && databaseFullPath !== databaseDisplayName ? databaseFullPath : undefined}
          >
            <Database className="h-3 w-3" />
            <span>{databaseDisplayName}</span>
          </div>
        )}
      </div>
    </div>
  );
}
